/**
 * Localisation.
 *
 * WHERE THE STRINGS ARE
 * `packages/shared/locales/*.json`, one file per language — NOT in this file.
 * They used to be two 600-line TypeScript objects here, which is fine for a
 * developer and useless for a translator: nobody can be asked to edit
 * TypeScript, and nobody could change one wrong word without a developer, a
 * build and a deploy. A translator now exports a spreadsheet from the admin
 * screen, edits it and uploads it; see apps/api/src/modules/translations.
 *
 * The key structure mirrors the domain rather than the screens, so one status
 * value has exactly one translation wherever it appears — a status rendered
 * differently in two places is a bug waiting to confuse an auditor.
 *
 * Server error codes are translated here too (the `error.*` namespace). The API
 * deliberately returns codes rather than prose, because the interface is
 * localised and the server is not.
 *
 * RESOLUTION ORDER
 *   database override -> bundled locale -> bundled English -> the key
 * The last step is unreachable in practice, because the parity guard refuses to
 * let English be incomplete. The step before it is the one that matters: eight
 * of the ten languages ship as empty stubs, so a Czech user sees English for
 * everything a translator has not reached yet, and never a raw `nav.dashboard`.
 *
 * THE SAFETY NET — DO NOT REMOVE EITHER HALF
 * This file used to declare `const el: typeof en`, which made the compiler fail
 * the build when Greek was missing a key English had. JSON has no such check,
 * so it is replaced by two things working together:
 *
 *   1. `elMatchesEnglish` below — the same structural check, still enforced by
 *      `tsc`, because TypeScript types a JSON import precisely.
 *   2. `apps/api/src/modules/translations/check.ts` — run by `pnpm typecheck`,
 *      which catches what the compiler cannot: extra keys, dropped `{{count}}`
 *      placeholders, half-translated plural families, and markup.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { LANGUAGES, type Language } from '@opencomply/shared';

import en from '@opencomply/shared/locales/en.json';
import el from '@opencomply/shared/locales/el.json';
import de from '@opencomply/shared/locales/de.json';
import fr from '@opencomply/shared/locales/fr.json';
import es from '@opencomply/shared/locales/es.json';
import it from '@opencomply/shared/locales/it.json';
import pl from '@opencomply/shared/locales/pl.json';
import ro from '@opencomply/shared/locales/ro.json';
import bg from '@opencomply/shared/locales/bg.json';
import cs from '@opencomply/shared/locales/cs.json';

/**
 * Half of the parity guard, and the direct descendant of `const el: typeof en`.
 *
 * If a key is added to en.json and not to el.json, this assignment stops
 * compiling and `pnpm -r typecheck` fails — exactly as it did when both lived
 * in TypeScript. The other half (placeholders, extra keys, plural families)
 * cannot be expressed in the type system and lives in the API's check script.
 *
 * The eight stub locales are deliberately NOT asserted here: they are empty on
 * purpose and fall back to English.
 */
const elMatchesEnglish: typeof en = el;
void elMatchesEnglish;

const resources = {
  en: { translation: en },
  el: { translation: el },
  de: { translation: de },
  fr: { translation: fr },
  es: { translation: es },
  it: { translation: it },
  pl: { translation: pl },
  ro: { translation: ro },
  bg: { translation: bg },
  cs: { translation: cs },
} satisfies Record<Language, { translation: object }>;

let stored: string | null = null;
try {
  stored = localStorage.getItem('oc-language');
} catch {
  // Storage can throw in private browsing. A missing preference is fine.
}
const initial = (LANGUAGES as readonly string[]).includes(stored ?? '')
  ? (stored as Language)
  : 'en';

void i18n.use(initReactI18next).init({
  resources,
  lng: initial,
  fallbackLng: 'en',
  supportedLngs: [...LANGUAGES],
  interpolation: { escapeValue: false }, // React escapes by default
  /**
   * An empty string is "not translated", not "translate to nothing".
   *
   * Without this, an override or a bundle entry of `""` renders a blank label
   * instead of falling back to English — the exact failure the fallback chain
   * exists to prevent, and far harder to spot in review than a missing key.
   */
  returnEmptyString: false,
  react: {
    /**
     * Re-render when a resource bundle is ADDED, not only when the language
     * changes. Database overrides arrive after the first paint (see below);
     * without this, a correction would sit in the store unrendered until
     * something else happened to re-render the tree.
     */
    bindI18nStore: 'added',
  },
});

// ---------------------------------------------------------------------------
//  Database overrides
// ---------------------------------------------------------------------------

/**
 * Strings a translator has corrected since the last deploy.
 *
 * Fetched rather than bundled, which is the entire point: a wrong word is
 * fixed by a translator uploading a file, not by a developer cutting a release.
 * The endpoint is unauthenticated because the sign-in screen is translated too,
 * and someone who cannot read it cannot sign in to fetch the strings that would
 * let them read it.
 *
 * This NEVER throws and never blocks rendering. If the API is unreachable, or
 * slow, or returns something unexpected, the app shows its bundled strings —
 * which are correct, merely a few words out of date. Holding the first paint
 * for a translation correction would be the wrong trade in every direction.
 */
async function fetchOverrides(language: string): Promise<void> {
  try {
    const response = await fetch(`/api/translations/${encodeURIComponent(language)}/overrides`);
    if (!response.ok) return;
    const payload = (await response.json()) as { messages?: unknown; count?: number };
    if (!payload.messages || typeof payload.messages !== 'object') return;
    // deep: merge into the existing bundle rather than replace it.
    // overwrite: an override beats the bundled string, which is its job.
    i18n.addResourceBundle(language, 'translation', payload.messages, true, true);
  } catch {
    // Offline, blocked, or the API is down. Bundled strings stand.
  }
}

/**
 * English is refreshed alongside the active language because it is the fallback
 * for every key the active language has not translated. A correction to an
 * English string must reach a Czech user too — for them it is most of the
 * interface.
 */
export function refreshOverrides(language: string = i18n.language || 'en'): void {
  void fetchOverrides('en');
  if (language !== 'en') void fetchOverrides(language);
}

refreshOverrides(i18n.language || 'en');
i18n.on('languageChanged', refreshOverrides);

/**
 * Keep `<html lang>` in step with the active language.
 *
 * index.html ships `lang="en"` and nothing updated it, so the attribute stayed
 * "en" while the interface was Greek. That is not cosmetic:
 *
 *  - `text-transform: uppercase` on Greek only drops the accent when the browser
 *    knows the text is Greek. With lang="en" the sidebar heading rendered as
 *    "ΟΡΓΑΝΙΣΜΌΣ"; Greek orthography requires "ΟΡΓΑΝΙΣΜΟΣ" (a capitalised
 *    single-accent word loses its tonos). Native readers see the accent as a
 *    spelling mistake.
 *  - A screen reader picks its voice and pronunciation rules from this
 *    attribute, so Greek was being read aloud by an English voice.
 */
function syncDocumentLanguage(lng: string) {
  document.documentElement.lang = lng;
}
syncDocumentLanguage(i18n.language || 'en');
i18n.on('languageChanged', syncDocumentLanguage);

/** Change the interface language and remember the choice. */
export function setLanguage(language: Language): void {
  void i18n.changeLanguage(language);
  try {
    localStorage.setItem('oc-language', language);
  } catch {
    // Storage may be blocked; the change still applies for this session.
  }
}

export default i18n;
export type TranslationKeys = typeof en;
